// Admin intake queue for membership applications (Issue #8).
// Lists submissions, lets an admin filter by status and advance each one
// through new -> contacted -> approved/declined, with optional notes.

import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchApplications, updateApplication, approveApplication } from '../api';

const STATUSES = ['new', 'contacted', 'approved', 'declined'];
const FILTERS = ['all', ...STATUSES];

const INTEREST_LABELS = {
  camping: 'Camping', fishing: 'Fishing', hunting: 'Hunting',
  hiking: 'Hiking', atv: 'ATV / Trail Riding', events: 'Family Events',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function interestList(csv) {
  if (!csv) return [];
  return String(csv).split(',').map((s) => s.trim()).filter(Boolean)
    .map((k) => INTEREST_LABELS[k] || k);
}

export default function AdminApplications() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [filter, setFilter] = useState('new');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [inviteLinks, setInviteLinks] = useState({}); // applicationId -> acceptUrl

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = filter === 'all' ? undefined : filter;
      const res = await fetchApplications(instance, account, status);
      if (res.ok) {
        setRows((res.data && res.data.applications) || []);
      } else {
        setError((res.data && res.data.error) || 'Could not load applications.');
      }
    } catch {
      setError('Network error loading applications.');
    } finally {
      setLoading(false);
    }
  }, [instance, account, filter]);

  useEffect(() => { load(); }, [load]);

  async function advance(row, status) {
    setBusyId(row.id);
    try {
      const res = await updateApplication(instance, account, row.id, { status });
      if (res.ok) {
        await load();
      } else {
        setError((res.data && res.data.error) || 'Update failed.');
      }
    } catch {
      setError('Network error updating application.');
    } finally {
      setBusyId(null);
    }
  }

  async function approve(row) {
    setBusyId(row.id);
    setError(null);
    try {
      const res = await approveApplication(instance, account, row.id);
      if (res.ok) {
        const url = res.data && (res.data.acceptUrl || (res.data.invite && res.data.invite.acceptUrl));
        if (url) setInviteLinks((m) => ({ ...m, [row.id]: url }));
        await load();
      } else {
        setError((res.data && res.data.error) || 'Approve failed.');
      }
    } catch {
      setError('Network error approving application.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card">
      <h2>Membership Applications</h2>

      <div className="app-filter">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={f === filter ? 'chip chip-active' : 'chip'}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
        <button className="chip" onClick={load} title="Refresh">↻</button>
      </div>

      {error && <p className="app-error" role="alert">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {!loading && rows.length === 0 && (
        <p className="muted">No applications{filter !== 'all' ? ` with status "${filter}"` : ''}.</p>
      )}

      {!loading && rows.length > 0 && (
        <ul className="app-list">
          {rows.map((r) => (
            <li className="app-row" key={r.id}>
              <div className="app-row-head">
                <strong>{r.full_name}</strong>
                <span className={`app-badge app-badge-${r.status}`}>{r.status}</span>
              </div>
              <div className="app-row-body">
                <p>
                  <a href={`mailto:${r.email}`}>{r.email}</a>
                  {r.phone ? ` · ${r.phone}` : ''}
                </p>
                {(r.city || r.household_size) && (
                  <p className="muted app-meta">
                    {r.city ? r.city : ''}
                    {r.city && r.household_size ? ' · ' : ''}
                    {r.household_size ? `Family of ${r.household_size}` : ''}
                  </p>
                )}
                {interestList(r.interests).length > 0 && (
                  <p className="app-interests-tags">
                    {interestList(r.interests).map((label) => (
                      <span key={label} className="app-tag">{label}</span>
                    ))}
                  </p>
                )}
                {r.heard_from && <p className="muted app-meta">Heard via: {r.heard_from}</p>}
                {r.message && <p className="app-msg">{r.message}</p>}
                <p className="muted app-meta">Submitted {fmtDate(r.created_at)}</p>
                {inviteLinks[r.id] && (
                  <p className="app-invite-link">
                    Invite created — <a href={inviteLinks[r.id]} target="_blank" rel="noreferrer">accept link</a>
                  </p>
                )}
              </div>
              <div className="app-row-actions">
                {r.status !== 'approved' && (
                  <button
                    className="chip chip-approve"
                    disabled={busyId === r.id}
                    onClick={() => approve(r)}
                    title="Approve and create a single-use invite"
                  >
                    ✓ Approve &amp; invite
                  </button>
                )}
                {STATUSES.filter((s) => s !== r.status && s !== 'approved').map((s) => (
                  <button
                    key={s}
                    className="chip"
                    disabled={busyId === r.id}
                    onClick={() => advance(r, s)}
                  >
                    → {s}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
